import { useMemo, useState } from 'react';
import { obterQrDoLote } from '../../api/lotes.js';
import type { QrDoLote } from '../../api/lotes.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { CampoSelecao } from '../../componentes/Campo.js';
import { Cartao } from '../../componentes/Cartao.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeEtiqueta } from '../../componentes/Icones.js';
import { BuscaDeLotes } from './BuscaDeLotes.js';
import { DesenhoDaEtiquetaDeLote } from './DesenhoDaEtiquetaDeLote.js';
import {
  COPIAS_MAXIMAS,
  TAMANHO_PADRAO,
  TAMANHOS_DE_ETIQUETA,
  tamanhoDeEtiqueta,
} from './etiqueta.js';
import type { LoteDaEtiqueta } from './etiqueta-lote.js';
import { etiquetasDeLoteParaImprimir, LIMITE_DE_LOTES } from './etiqueta-lote.js';
import { FolhaDeEtiquetas } from './FolhaDeImpressao.js';
import { OrientacaoDeImpressao } from './OrientacaoDeImpressao.js';
import { PreviaDasEtiquetasDeLote } from './PreviaDasEtiquetasDeLote.js';
import { useQrPorCodigo } from './useQrPorCodigo.js';
import './GeradorDeEtiquetas.css';

const OPCOES_DE_TAMANHO = TAMANHOS_DE_ETIQUETA.map((tamanho) => ({
  valor: tamanho.id,
  rotulo: tamanho.rotulo,
}));

const OPCOES_DE_COPIAS = Array.from({ length: COPIAS_MAXIMAS }, (_, indice) => {
  const copias = indice + 1;
  return {
    valor: String(copias),
    rotulo: copias === 2 ? '2 — frente e lateral' : String(copias),
  };
});

function plural(quantidade: number, singular: string, varias: string): string {
  return `${String(quantidade)} ${quantidade === 1 ? singular : varias}`;
}

/**
 * Montar a lista de lotes, conferir as etiquetas e mandar para a impressão.
 *
 * O QR carrega só o `codLot` (`GET /lotes/qrcode`); produto, derivação e
 * validade vão no texto legível. O mesmo `codLot` de dois produtos tem a
 * mesma matriz, então o QR é pedido uma vez por `codLot`.
 */
export function GeradorDeEtiquetasDeLote({
  buscaInicial = '',
}: {
  /** Busca já feita ao abrir (o `codPro` que veio no link). */
  readonly buscaInicial?: string;
}) {
  const [lotes, definirLotes] = useState<readonly LoteDaEtiqueta[]>([]);
  const [idDoTamanho, definirIdDoTamanho] = useState<string>(TAMANHO_PADRAO);
  const [copias, definirCopias] = useState(1);
  const [acimaDoLimite, definirAcimaDoLimite] = useState(false);

  const codigos = useMemo(() => [...new Set(lotes.map((item) => item.lote.codLot))], [lotes]);
  const { estados, tentarDeNovo } = useQrPorCodigo(codigos, obterQrDoLote);
  const tamanho = tamanhoDeEtiqueta(idDoTamanho);

  const qrDe = (item: LoteDaEtiqueta): QrDoLote | null => {
    const estado = estados.get(item.lote.codLot);
    return estado?.situacao === 'pronto' ? estado.qr : null;
  };

  const prontos = lotes.filter((item) => qrDe(item) !== null);
  const semQr = lotes.filter((item) => estados.get(item.lote.codLot)?.situacao === 'falhou').length;
  const gerando = lotes.length - prontos.length - semQr;
  const etiquetas = etiquetasDeLoteParaImprimir(
    prontos.map((item) => item.chave),
    copias,
  );
  const porChave = new Map(lotes.map((item) => [item.chave, item]));

  function acrescentar(item: LoteDaEtiqueta) {
    if (lotes.some((atual) => atual.chave === item.chave)) {
      return;
    }
    if (lotes.length >= LIMITE_DE_LOTES) {
      definirAcimaDoLimite(true);
      return;
    }
    definirAcimaDoLimite(false);
    definirLotes([...lotes, item]);
  }

  return (
    <div className="gerador-etiquetas">
      <div className="gerador-etiquetas__colunas">
        <Cartao titulo="Lotes">
          <div className="gerador-etiquetas__entrada">
            <BuscaDeLotes
              naLista={lotes.map((item) => item.chave)}
              aoEscolher={acrescentar}
              buscaInicial={buscaInicial}
            />
            {acimaDoLimite && (
              <AvisoDaTela tom="atencao">
                {`Até ${String(LIMITE_DE_LOTES)} lotes por impressão. Imprima estes antes de acrescentar outros.`}
              </AvisoDaTela>
            )}
          </div>
        </Cartao>

        <Cartao titulo="Impressão">
          <div className="gerador-etiquetas__impressao">
            <CampoSelecao
              rotulo="Tamanho da etiqueta"
              valor={tamanho.id}
              opcoes={OPCOES_DE_TAMANHO}
              aoMudar={definirIdDoTamanho}
              ajuda="Uma etiqueta por página, no tamanho exato do rolo"
            />
            <CampoSelecao
              rotulo="Cópias por lote"
              valor={String(copias)}
              opcoes={OPCOES_DE_COPIAS}
              aoMudar={(valor) => {
                definirCopias(Number(valor));
              }}
            />

            {semQr > 0 && (
              <AvisoDaTela tom="atencao">
                {semQr === 1
                  ? '1 lote sem QR fica fora da impressão.'
                  : `${String(semQr)} lotes sem QR ficam fora da impressão.`}
              </AvisoDaTela>
            )}

            {etiquetas.length > 0 && gerando === 0 && (
              <Botao
                variante="primario"
                onClick={() => {
                  window.print();
                }}
              >
                {`Imprimir ${plural(etiquetas.length, 'etiqueta', 'etiquetas')}`}
              </Botao>
            )}
          </div>
        </Cartao>
      </div>

      {lotes.length === 0 ? (
        <Cartao semRespiro>
          <EstadoVazio
            tom="neutro"
            icone={<IconeEtiqueta tamanho={22} />}
            titulo="Nenhum lote na lista"
            detalhe="Busque pelo código do lote ou do produto. Cada lote ganha uma etiqueta com o QR do lote, o código do lote em letra grande e, menores, o produto, a derivação e a validade."
          />
        </Cartao>
      ) : (
        <PreviaDasEtiquetasDeLote
          lotes={lotes}
          estados={estados}
          tamanho={tamanho}
          aoRemover={(chave) => {
            definirLotes((atuais) => atuais.filter((item) => item.chave !== chave));
          }}
          aoTentarDeNovo={tentarDeNovo}
        />
      )}

      <OrientacaoDeImpressao />

      <FolhaDeEtiquetas tamanho={tamanho}>
        {etiquetas.map((etiqueta) => {
          const item = porChave.get(etiqueta.chaveDoLote);
          const qr = item === undefined ? null : qrDe(item);
          return item === undefined || qr === null ? null : (
            <div key={etiqueta.chave} className="folha-etiquetas__pagina">
              <DesenhoDaEtiquetaDeLote tamanho={tamanho} qr={qr} lote={item.lote} emMilimetros />
            </div>
          );
        })}
      </FolhaDeEtiquetas>
    </div>
  );
}
